import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import BroadcastDrawer from './Drawer';
import List from './List';
import BroadcastHeader from './Header';
import WarmupPlanDetails from './WarmupPlanDetails';
import Graph from './Graph';
import MessageDrawer from './MessageDrawer';
import DuplicateBroadcastModal from './DuplicateBroadcastModal';
import { useDuplicateBroadcast } from '../../hooks/useDuplicateBroadcast';

export default function BroadcastsPage() {
  const { playlistId, broadcastId } = useParams();
  const navigate = useNavigate();

  const [broadcasts, setBroadcasts] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [broadcastToEdit, setBroadcastToEdit] = useState(null);
  // Ajouter après vos useState existants
  const [broadcastToDuplicate, setBroadcastToDuplicate] = useState(null);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);

  const [testEmails, setTestEmails] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [sending, setSending] = useState(false);

  const [currentBroadcastName, setCurrentBroadcastName] = useState('');
  const [currentBroadcastCurrentDay, setCurrentBroadcastCurrentDay] =
    useState(1);

  const [loadingCardId, setLoadingCardId] = useState(null);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const token = localStorage.getItem('prae.jwt');

  // Ajouter après vos useState existants
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [smtpProviders, setSmtpProviders] = useState([]);
  const [categories, setCategories] = useState([]);

  const { duplicateBroadcast, isLoading: isDuplicating } =
    useDuplicateBroadcast();

  const fetchBroadcasts = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/playlists/${playlistId}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setBroadcasts(data);
    } catch (err) {
      // optionnel : set erreur
    }
  }, [playlistId]);

  useEffect(() => {
    if (playlistId) {
      fetchBroadcasts();
      loadReferenceData();
    }
  }, [playlistId, fetchBroadcasts]);

  const loadReferenceData = async () => {
    try {
      const [providersRes, categoriesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/smtp-providers`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${process.env.REACT_APP_API_URL}/categories`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (providersRes.ok) {
        const providersData = await providersRes.json();
        setSmtpProviders(providersData);
      }

      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error('Erreur chargement données de référence:', error);
    }
  };

  useEffect(() => {
    if (broadcasts.length > 0 && !broadcastId) {
      navigate(
        `/broadcasts/playlists/${playlistId}/broadcasts/${broadcasts[0].id}`,
        { replace: true }
      );
    }
    if (broadcasts.length > 0 && broadcastId) {
      const current = broadcasts.find(b => b.id === broadcastId);
      if (current) {
        setCurrentBroadcastName(current.name);
        setCurrentBroadcastCurrentDay(current.current_day || 1);
      }
    }
  }, [broadcasts, broadcastId, playlistId, navigate]);

  const handleCreateBroadcast = () => {
    setBroadcastToEdit(null);
    setDrawerOpen(true);
  };
  const handleEditBroadcast = broadcast => {
    setBroadcastToEdit(broadcast);
    setDrawerOpen(true);
  };
  const handleDrawerSave = async () => {
    await fetchBroadcasts();
    setDrawerOpen(false);
    setBroadcastToEdit(null);
    setBroadcastToDuplicate(null);
  };
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setBroadcastToEdit(null);
    setBroadcastToDuplicate(null);
  };
  const handleSelectBroadcast = id => {
    navigate(`/broadcasts/playlists/${playlistId}/broadcasts/${id}`);
  };

  const handleScheduleChange = async (broadcastId, dateStr) => {
    setLoadingCardId(broadcastId);
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/schedule`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ scheduled_date: dateStr }),
        }
      );
      await fetchBroadcasts();
    } catch (err) {
      console.error('Erreur lors de la mise à jour du planning', err);
    }
    setLoadingCardId(null);
  };

  const handleDeleteBroadcast = async broadcastId => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    setLoadingCardId(broadcastId);
    await fetch(`${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}`, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'multipart/form-data',
      },
    });
    await fetchBroadcasts();
    setLoadingCardId(null);
  };

  const handlePauseBroadcast = async broadcastId => {
    if (!window.confirm('Voulez-vous vraiment mettre en pause ce broadcast ?'))
      return;
    setLoadingCardId(broadcastId);
    await fetch(
      `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/pause`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    await fetchBroadcasts();
    setLoadingCardId(null);
  };

  const handleResumeBroadcast = async broadcastId => {
    setLoadingCardId(broadcastId);
    await fetch(
      `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/resume`,
      {
        method: 'POST',
      }
    );
    await fetchBroadcasts();
    setLoadingCardId(null);
  };

  const handleSendTests = async () => {
    setSending(true);
    setTestResult(null);
    const emails = testEmails
      .split(';')
      .map(e => e.trim())
      .filter(Boolean);

    const successes = [];
    const failures = [];

    await Promise.all(
      emails.map(async email => {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/broadcasts/queue/test-jobs`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                email,
                broadcastId,
                day: 0,
                slot: 0,
              }),
            }
          );
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Erreur serveur');
          successes.push(data.jobId);
        } catch (err) {
          failures.push({ email, error: err.message });
        }
      })
    );

    setTestResult({ successes, failures });
    setSending(false);
  };

  const handleOpenDuplicateModal = broadcast => {
    setSelectedBroadcast(broadcast);
    setShowDuplicateModal(true);
  };

  const handleDuplicateBroadcast = broadcast => {
    setBroadcastToDuplicate(broadcast);
    setBroadcastToEdit(null); // S'assurer qu'on n'est pas en mode édition
    setDrawerOpen(true);
  };

  return (
    <div className="flex mt-10">
      <div className="w-1/3 border-r border-gray-300 p-4 h-screen">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Broadcasts</h2>
          <button
            onClick={handleCreateBroadcast}
            className="bg-black text-white p-2 rounded-full hover:bg-gray-800"
          >
            <FaPlus size={16} />
          </button>
        </div>
        <List
          playlistId={playlistId}
          broadcasts={broadcasts}
          setBroadcasts={setBroadcasts}
          selectedBroadcastId={broadcastId}
          onSelectBroadcast={handleSelectBroadcast}
          onEditBroadcast={handleEditBroadcast}
          onDuplicateBroadcast={handleDuplicateBroadcast} // NOUVEAU
          onScheduleChange={handleScheduleChange}
          onDelete={handleDeleteBroadcast}
          onPause={handlePauseBroadcast}
          onResume={handleResumeBroadcast}
          loadingCardId={loadingCardId}
        />
      </div>

      <div className="w-2/3 p-6 bg-white">
        <BroadcastHeader
          broadcastName={currentBroadcastName}
          onOpenMessageDrawer={() => setIsMessageDrawerOpen(true)}
          setShowAI={() => setShowAI(true)}
          playlistId={playlistId}
          broadcastId={broadcastId}
        />

        <div className="mt-6 mb-8 p-4 border rounded">
          <label className="block font-medium mb-2">
            Destinataires de test (séparés par <code>;</code>)
          </label>
          <textarea
            rows={2}
            className="w-full border px-2 py-1 rounded mb-2"
            placeholder="test1@example.com; test2@example.com"
            value={testEmails}
            onChange={e => setTestEmails(e.target.value)}
            disabled={sending}
          />
          <button
            onClick={handleSendTests}
            disabled={sending || !testEmails.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50"
          >
            {sending ? 'Envoi en cours…' : 'Envoyer tests'}
          </button>

          {testResult && (
            <div className="mt-4 text-sm">
              {testResult.successes.length > 0 && (
                <p className="text-green-700">
                  ✔️ {testResult.successes.length} job(s) en file d’attente :
                  {testResult.successes.join(', ')}
                </p>
              )}
              {testResult.failures.length > 0 && (
                <div className="text-red-700">
                  ❌ Erreurs pour :
                  <ul className="list-disc ml-5">
                    {testResult.failures.map(f => (
                      <li key={f.email}>
                        {f.email} : {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <Graph />

        <WarmupPlanDetails
          broadcastId={broadcastId}
          currentDay={currentBroadcastCurrentDay}
        />
      </div>

      <BroadcastDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        playlistId={playlistId}
        broadcastToEdit={broadcastToEdit}
        broadcastToDuplicate={broadcastToDuplicate}
        onSave={handleDrawerSave}
      />

      <MessageDrawer
        playlistId={playlistId}
        isOpen={isMessageDrawerOpen}
        onClose={() => setIsMessageDrawerOpen(false)}
      />
      <DuplicateBroadcastModal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setSelectedBroadcast(null);
        }}
        originalBroadcast={selectedBroadcast}
        onDuplicate={handleDuplicateBroadcast}
        smtpProviders={smtpProviders}
        categories={categories}
      />
    </div>
  );
}
